import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Building2,
  Store,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  MapPin,
  ArrowRight,
  Box,
  Sparkles,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { toast } from 'sonner';

const COLORS = ['#00F0FF', '#4F46E5', '#10B981', '#F59E0B', '#EF4444'];

export default function DashboardHome() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [malls, setMalls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, mallsRes] = await Promise.all([
        api.get('/analytics/overview'),
        api.get('/malls/'),
      ]);
      setAnalytics(analyticsRes.data);
      setMalls(mallsRes.data.slice(0, 6));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Mock trend data for charts
  const trendData = [
    { month: 'Jan', occupancy: 85, revenue: 4200 },
    { month: 'Feb', occupancy: 87, revenue: 4500 },
    { month: 'Mar', occupancy: 86, revenue: 4300 },
    { month: 'Apr', occupancy: 89, revenue: 4800 },
    { month: 'May', occupancy: 91, revenue: 5100 },
    { month: 'Jun', occupancy: 90, revenue: 4900 },
  ];

  const categoryData = analytics?.category_breakdown
    ? Object.entries(analytics.category_breakdown).map(([name, value]) => ({
        name: name.replace('_', ' ').toUpperCase(),
        value,
      }))
    : [];

  const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendUp }) => (
    <Card className="stat-card animate-fade-in" data-testid={`stat-${title.toLowerCase().replace(' ', '-')}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground font-medium">{title}</p>
            <p className="text-2xl font-bold mt-1 font-mono">{value}</p>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
          </div>
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon className="w-5 h-5 text-primary" />
          </div>
        </div>
        {trend && (
          <div className="flex items-center gap-1 mt-3">
            {trendUp ? (
              <TrendingUp className="w-4 h-4 text-emerald-500" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-500" />
            )}
            <span className={`text-sm font-medium ${trendUp ? 'text-emerald-500' : 'text-red-500'}`}>
              {trend}
            </span>
            <span className="text-xs text-muted-foreground">vs last month</span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-home">
      {/* Welcome Section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Welcome back, {user?.name?.split(' ')[0]}</h2>
          <p className="text-muted-foreground">
            Here's what's happening across your portfolio today.
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="btn-secondary"
            onClick={() => navigate('/digital-twin')}
            data-testid="view-twin-btn"
          >
            <Box className="w-4 h-4 mr-2" />
            3D Digital Twin
          </Button>
          <Button
            className="btn-primary"
            onClick={() => navigate('/ai-insights')}
            data-testid="ai-insights-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            AI Insights
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="bento-grid">
        <StatCard
          title="Total Malls"
          value={analytics?.total_malls || 0}
          subtitle="Across all groups"
          icon={Building2}
          trend="+2"
          trendUp
        />
        <StatCard
          title="Total Shops"
          value={(analytics?.total_shops || 0).toLocaleString()}
          subtitle={`${(analytics?.total_area_sqft || 0).toLocaleString()} sq ft`}
          icon={Store}
          trend="+45"
          trendUp
        />
        <StatCard
          title="Occupancy Rate"
          value={`${(analytics?.occupancy_rate || 0).toFixed(1)}%`}
          subtitle={`${(100 - (analytics?.vacancy_rate || 0)).toFixed(1)}% leased`}
          icon={TrendingUp}
          trend="+2.3%"
          trendUp
        />
        <StatCard
          title="Avg Rent/sq ft"
          value={`₹${(analytics?.avg_rent_per_sqft || 0).toFixed(0)}`}
          subtitle="Monthly average"
          icon={DollarSign}
          trend="+5.2%"
          trendUp
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Occupancy Trend */}
        <Card className="glass-card lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Occupancy Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData}>
                  <defs>
                    <linearGradient id="occupancyGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00F0FF" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#00F0FF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" />
                  <YAxis stroke="hsl(var(--muted-foreground))" domain={[80, 100]} />
                  <Tooltip
                    contentStyle={{
                      background: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="occupancy"
                    stroke="#00F0FF"
                    strokeWidth={2}
                    fill="url(#occupancyGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Category Distribution */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-base">Category Mix</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2 mt-4">
              {categoryData.slice(0, 4).map((cat, i) => (
                <div key={cat.name} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: COLORS[i % COLORS.length] }}
                    />
                    <span className="text-muted-foreground capitalize">{cat.name}</span>
                  </div>
                  <span className="font-mono">{cat.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Malls List */}
      <Card className="glass-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Malls Overview</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate('/malls')} data-testid="view-all-malls">
            View all
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {malls.map((mall, index) => (
              <div
                key={mall.id}
                className="p-4 rounded-lg border border-border/40 hover:border-primary/30 transition-colors cursor-pointer animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
                onClick={() => navigate(`/malls/${mall.id}`)}
                data-testid={`mall-card-${mall.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-sm">{mall.name}</h3>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                      <MapPin className="w-3 h-3" />
                      {mall.city}, {mall.state}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {mall.floors} floors
                  </Badge>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Occupancy</span>
                    <span className="font-mono">{mall.occupancy_rate?.toFixed(0)}%</span>
                  </div>
                  <Progress value={mall.occupancy_rate || 0} className="h-1.5" />
                </div>
                <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
                  <span>{mall.shop_count} shops</span>
                  <span>{(mall.total_area_sqft / 1000).toFixed(0)}K sq ft</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Status Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.entries(analytics?.status_breakdown || {}).map(([status, count]) => (
          <Card key={status} className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`status-dot ${status}`} />
                  <span className="capitalize text-sm">{status.replace('_', ' ')}</span>
                </div>
                <span className="text-xl font-bold font-mono">{count}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
